// @ts-check
/**
 * **共通のトランスフォーム**（Issue #523。ユーザー要望 2026-09-24）。
 *
 * 「汎用パラメータとして **x, y, z / スケール / 透明度 / 回転 / アンカーポイント**、
 * テキストの時はフォントサイズとフォント みたいな、**基本的なパラメータとオブジェクト固有の
 * パラメータ**」—— よくある編集ソフト（After Effects など）の
 *
 * ```
 * レイヤー
 * ├ トランスフォーム … **どのレイヤーにも付く**
 * └ <タイプ> のオプション … 固有
 * ```
 *
 * という形。テーマは**固有のものだけ**を宣言すればよく、位置・大きさ・回転・不透明度は
 * 名前も範囲も**全部の部品で同じ**になる（今までは `plateW` / `plateTop` のように
 * 部品ごとに違う名前だった）。
 *
 * **素の大きさとトランスフォームは別**（AE の「レイヤーの中身」と「トランスフォーム」と同じ）——
 * `plateW` は枠そのものの大きさで、`tf/hud:plate/scale` はその上に掛かる。
 *
 * ## 空間によって意味が変わる（語彙は 1 つ）
 *
 * | 空間 | 位置 | z | 回転 |
 * |---|---|---|---|
 * | 盤の上（`board`） | 平面の座標のずらし | **盤からの浮き** | 盤の上での回し |
 * | 舞台のピクセル・HUD（`screen`） | 舞台のピクセルのずらし | 出さない | 画面の中での回し |
 *
 * ## 値の持ち方は**平たい鍵**
 *
 * `tf/<part>/<prop>`。入れ子にしないのは、上書きの重ね方・既定との差分・書き出し・
 * localStorage が**今のまま動く**から（`resolveStyle` / `changedValues` / `saveStyle` を変えずに済む）。
 */

/**
 * @typedef {object} Transform
 * @property {number} anchorX
 * @property {number} anchorY
 * @property {number} x
 * @property {number} y
 * @property {number} z
 * @property {number} scale
 * @property {number} rotation
 * @property {number} opacity
 */

/**
 * **共通のプロパティ**（この表が一次情報。足せば全部の部品に付く）。
 * `board: true` のものは盤の上の部品にだけ出す（舞台のピクセルに浮きは無い）。
 */
export const TRANSFORM_PROPS = Object.freeze([
  { key: 'anchorX', label: 'アンカー X', min: 0, max: 1, step: 0.05, def: 0.5 },
  { key: 'anchorY', label: 'アンカー Y', min: 0, max: 1, step: 0.05, def: 0.5 },
  { key: 'x', label: '位置 X', min: -1920, max: 1920, step: 1, def: 0 },
  { key: 'y', label: '位置 Y', min: -1080, max: 1080, step: 1, def: 0 },
  { key: 'z', label: '浮き', min: -200, max: 400, step: 1, def: 0, board: true },
  { key: 'scale', label: 'スケール', min: 0.1, max: 4, step: 0.01, def: 1 },
  { key: 'rotation', label: '回転', min: -180, max: 180, step: 1, def: 0 },
  { key: 'opacity', label: '不透明度', min: 0, max: 1, step: 0.01, def: 1 },
].map((p) => Object.freeze(p)));

/** GUI のフォルダの見出し（`schema` の `group`）。**固有より先に出す**のはこの名前で見分ける */
export const TRANSFORM_GROUP = 'トランスフォーム';

/** 素のトランスフォーム（何もしない） */
export const IDENTITY = Object.freeze(
  /** @type {Transform} */ (/** @type {any} */ (Object.fromEntries(TRANSFORM_PROPS.map((p) => [p.key, p.def])))),
);

/** 値の鍵 */
export const transformKey = (part, prop) => `tf/${part}/${prop}`;

/** その鍵がトランスフォームのものか（`{ part, prop }` か null） */
export function parseTransformKey(key) {
  const m = /^tf\/(.+)\/([a-zA-Z]+)$/.exec(String(key ?? ''));
  return m ? { part: m[1], prop: m[2] } : null;
}

const propsFor = (space) => TRANSFORM_PROPS.filter((p) => !p.board || space === 'board');

/**
 * 部品の一覧 → **宣言**（`core/theme` が `schema` に混ぜる）。
 * @param {ReadonlyArray<{part: string, space?: string}>} parts
 */
export function transformSchema(parts) {
  const out = {};
  (parts ?? []).forEach(({ part, space }) => {
    propsFor(space).forEach((p) => {
      out[transformKey(part, p.key)] = {
        type: 'number', label: p.label, min: p.min, max: p.max, step: p.step,
        part, group: TRANSFORM_GROUP,
      };
    });
  });
  return out;
}

/**
 * 部品の一覧 → **既定値**。
 * @param {ReadonlyArray<{part: string, space?: string}>} parts
 */
export function transformStyle(parts) {
  const out = {};
  (parts ?? []).forEach(({ part, space }) => {
    propsFor(space).forEach((p) => { out[transformKey(part, p.key)] = p.def; });
  });
  return out;
}

/**
 * **親子の合成**（Issue #530）。演出の層を別の層にぶら下げて**まとまりごと動かす**ため。
 *
 * ここが扱うのは**相似変換**（一様な拡大 ＋ 回転 ＋ 平行移動）だけなので、行列にせず値のまま合成できる
 * （`scale` が 1 つしか無いのでせん断は出ない）。
 * **子のずらしは親の回転と倍率を受ける** —— 受けないと、親を回したときに子だけが元の向きに置き去りになる。
 *
 * @param {Transform} parent
 * @param {Transform} child
 * @returns {Transform}
 */
export function composeTransform(parent, child) {
  const a = rad(parent.rotation);
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const x = child.x * parent.scale;
  const y = child.y * parent.scale;
  return {
    ...child,
    x: parent.x + x * cos - y * sin,
    y: parent.y + x * sin + y * cos,
    z: parent.z + child.z,
    scale: parent.scale * child.scale,
    rotation: parent.rotation + child.rotation,
    opacity: parent.opacity * child.opacity,
  };
}

/** その部品の今のトランスフォーム（欠けていれば素の値） */
export function transformOf(style, part) {
  const out = { ...IDENTITY };
  TRANSFORM_PROPS.forEach((p) => {
    const v = style?.[transformKey(part, p.key)];
    if (Number.isFinite(v)) out[p.key] = v;
  });
  return /** @type {Transform} */ (out);
}

/** 何も動かさないか（素のままなら当てる処理ごと飛ばせる） */
export const isIdentity = (tf) => TRANSFORM_PROPS.every((p) => (tf?.[p.key] ?? p.def) === p.def);

/**
 * **箱に当てる**。**アンカーを動かさずに**拡大してから位置をずらす。
 *
 * `rotation` / `opacity` / `scale` / アンカーは**箱に載せて返す**（当て方が置き場で違うため）:
 *
 * | 置き場 | 大きさ | 回転 |
 * |---|---|---|
 * | DOM（HUD） | 返った `w` / `h` をそのまま（`core/hud::partStyle`） | `transform: rotate()` ＋ `transform-origin` |
 * | three（盤・板） | **`scale` を `root.scale` に**（幾何は素のまま ＝ テクスチャを上げ直さない） | `root.rotation` |
 *
 * **回転で箱の位置は変えない** —— アンカーまわりに回すのは描く側（`transform-origin` / 中心のずらし）。
 */
export function applyToBox(box, tf) {
  if (!box) return box;
  const t = { ...IDENTITY, ...(tf ?? {}) };
  const ax = box.x + box.w * t.anchorX;
  const ay = box.y + box.h * t.anchorY;
  const w = box.w * t.scale;
  const h = box.h * t.scale;
  return {
    ...box,
    x: ax - w * t.anchorX + t.x,
    y: ay - h * t.anchorY + t.y,
    w,
    h,
    scale: t.scale,
    rotation: t.rotation,
    opacity: t.opacity,
    anchorX: t.anchorX,
    anchorY: t.anchorY,
  };
}

/** 箱の中のアンカーの点 */
export const anchorPoint = (box, tf) => {
  const t = { ...IDENTITY, ...(tf ?? {}) };
  return [box.x + box.w * t.anchorX, box.y + box.h * t.anchorY];
};

/**
 * **舞台のピクセルでの回し**（CSS の `rotate()` ＝ three の `rotation.z` と同じ向き。y が下なので時計回り）。
 */
export function rotateScreen([x, y], [ox, oy], rad) {
  const c = Math.cos(rad); const s = Math.sin(rad);
  const dx = x - ox; const dy = y - oy;
  return [ox + dx * c - dy * s, oy + dx * s + dy * c];
}

/**
 * **盤の上での回し**（three の `root.rotation.y`。平面の紙は XZ へ倒してあるので、
 * 平面の座標 (u, v) では行列が転置になる）。**`place()` と同じ向き**でないと、
 * 回した板の中心がずれる。
 */
export function rotatePlane([u, v], [ou, ov], rad) {
  const c = Math.cos(rad); const s = Math.sin(rad);
  const du = u - ou; const dv = v - ov;
  return [ou + du * c + dv * s, ov - du * s + dv * c];
}

/** 度 → ラジアン */
export const rad = (deg) => (Number.isFinite(deg) ? deg : 0) * Math.PI / 180;

/**
 * **箱 → 置き場に渡す値**（純関数。`stage/` の層が使う）。
 *
 * 素の箱と `space`（`board` / `screen`）を渡すと、**回し込みまで終わった中心**・素の大きさ・
 * `root.scale` に渡す倍率・足す回し（ラジアン）・掛ける濃さを返す。
 *
 * @param {{x:number,y:number,w:number,h:number}} box 素の箱
 * @param {*} tf トランスフォーム
 * @param {'board'|'screen'} space
 */
export function placeBox(box, tf, space = 'screen') {
  const t = { ...IDENTITY, ...(tf ?? {}) };
  const moved = applyToBox(box, t);
  const a = /** @type {[number, number]} */ (anchorPoint(moved, t));
  const c = /** @type {[number, number]} */ ([moved.x + moved.w / 2, moved.y + moved.h / 2]);
  const r = rad(t.rotation);
  const center = r === 0 ? c : (space === 'board' ? rotatePlane(c, a, r) : rotateScreen(c, a, r));
  return { center, size: { w: box.w, h: box.h }, scale: t.scale, rot: r, opacity: t.opacity };
}
